"use client";

import { Elements } from "@stripe/react-stripe-js";
import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { PAYPAL_CUSTOM_PAYMENT_METHOD_ID } from "~/lib/payment-constants";
import { PaymentForm, type PaymentFormProps } from "./payment-form";
import { PaymentMetadata } from "~/types/payment";
import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { getStripePromise } from "~/lib/stripe/client";
import { PaymentErrorState } from "./payment-error-state";

export interface StripePaymentElementsProps extends Omit<
  PaymentFormProps,
  "paymentMetadata"
> {
  paymentMetadata?: PaymentMetadata;
  allowPayPal?: boolean;
}

export function StripePaymentElements(props: StripePaymentElementsProps) {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [stripeLoadStatus, setStripeLoadStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [stripeLoadAttempt, setStripeLoadAttempt] = useState(0);
  const session = useSession();

  useEffect(() => {
    let isCancelled = false;
    setStripe(null);
    setStripeLoadStatus("loading");

    void getStripePromise()
      .then((loadedStripe) => {
        if (isCancelled) {
          return;
        }

        if (!loadedStripe) {
          setStripeLoadStatus("error");
          return;
        }

        setStripe(loadedStripe);
        setStripeLoadStatus("ready");
      })
      .catch((error: unknown) => {
        console.error("Failed to load Stripe.js", error);

        if (!isCancelled) {
          setStripeLoadStatus("error");
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [stripeLoadAttempt]);

  const getThemeColor = (token: string, fallback: string) =>
    typeof window !== "undefined"
      ? getComputedStyle(document.documentElement)
          .getPropertyValue(token)
          .trim() || fallback
      : fallback;

  const brandHsl = `hsl(${getThemeColor("--brand-action", "175.6 78.5% 23.7%")})`;
  const backgroundHsl = `hsl(${getThemeColor("--background", "41 53% 94.1%")})`;
  const foregroundHsl = `hsl(${getThemeColor("--foreground", "168 18% 11%")})`;
  const mutedForegroundHsl = `hsl(${getThemeColor("--muted-foreground", "40 10% 35%")})`;
  const borderHsl = `hsl(${getThemeColor("--border", "37.9 26.8% 86.1%")})`;

  const options: StripeElementsOptions = {
    clientSecret: props.clientSecret,
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: brandHsl,
        colorText: foregroundHsl,
        colorTextSecondary: mutedForegroundHsl,
        colorBackground: backgroundHsl,
        colorDanger: "#ef4444",
      },
      rules: {
        ".Tab": {
          backgroundColor: backgroundHsl,
          border: `1px solid ${borderHsl}`,
          color: mutedForegroundHsl,
        },
        ".Tab:hover": {
          backgroundColor: `hsl(${getThemeColor("--secondary", "36 36.2% 86.5%")})`,
        },
        ".Tab--selected, .Tab--selected:hover": {
          backgroundColor: backgroundHsl,
          border: `1px solid ${brandHsl}`,
          boxShadow: `0 0 0 1px ${brandHsl}`,
          color: foregroundHsl,
        },
        ".Input": {
          backgroundColor: backgroundHsl,
          border: `1px solid ${borderHsl}`,
          boxShadow: "none",
        },
        ".Input:focus": {
          border: `1px solid ${brandHsl}`,
          boxShadow: `0 0 0 1px ${brandHsl}`,
        },
        ".Label": {
          color: foregroundHsl,
        },
      },
    },
    // Stripe nema srpsku lokalizaciju; hrvatska je najbliža podržana.
    locale: "hr",
    customPaymentMethods:
      props.allowPayPal === false
        ? []
        : [
            {
              id: PAYPAL_CUSTOM_PAYMENT_METHOD_ID,
              options: {
                type: "static",
                subtitle: "Plati svojim PayPal nalogom",
              },
            },
          ],
  };

  const billingDetails = {
    name: session.data?.user.name ?? undefined,
    email: session.data?.user.email ?? undefined,
  };

  if (stripeLoadStatus === "error") {
    return (
      <PaymentErrorState
        title="Plaćanje trenutno nije dostupno"
        errorMessage="Ne možemo da pokrenemo plaćanje. Proveri internet vezu, isključi ad blocker za ovu stranicu ili probaj iz drugog browsera."
        helperText="Tvoja sesija nije zakazana i ništa nije naplaćeno."
        actionLabel="Pokušaj ponovo"
        onAction={() => setStripeLoadAttempt((attempt) => attempt + 1)}
      />
    );
  }

  if (stripeLoadStatus !== "ready" || !stripe) {
    return (
      <div
        className="rounded-lg border border-border bg-card p-6 text-center"
        role="status"
        aria-live="polite"
      >
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand" />
        <p className="text-sm text-muted-foreground">Učitavanje plaćanja...</p>
      </div>
    );
  }

  return (
    <Elements stripe={stripe} options={options}>
      <PaymentForm {...props} billingDetails={billingDetails} />
    </Elements>
  );
}
